import { useEffect, useState } from "react";
import { api, API_BASE } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Download, FileSpreadsheet, Info } from "lucide-react";

const download = async (path, params, filename) => {
  const token = localStorage.getItem("khn_token");
  const url = new URL(`${API_BASE}${path}`);
  Object.entries(params).forEach(([k, v]) => v && url.searchParams.set(k, v));
  const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
  const blob = await res.blob();
  const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = filename; a.click();
};

const Row = ({ label, value, bold, sub, indent }) => (
  <div className={`flex justify-between gap-4 py-2 ${bold ? "font-semibold text-slate-900" : "text-slate-700"} ${sub ? "text-[13px] text-slate-500" : ""}`}>
    <span className={indent ? "pl-4" : ""}>{label}</span>
    <span className="font-mono whitespace-nowrap">{value}</span>
  </div>
);

const Section = ({ title, children }) => (
  <div className="card-flat p-5">
    <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-2 border-b border-slate-100 pb-2">{title}</div>
    <div className="divide-y divide-slate-100">{children}</div>
  </div>
);

const emptyData = {
  tahun: new Date().getFullYear(), wp: {}, ptkp: { status: "TK/0", total: 0, tanggungan: 0 },
  metode_usaha: "umkm", gaji_bruto: 0, biaya_jabatan: 0, gaji_neto: 0, usaha_bruto: 0, usaha_neto: 0,
  penghasilan_neto_lain: 0, penghasilan_neto: 0, pkp: 0, pph_progresif: 0, pph_progresif_lapisan: [],
  final: { umkm: { total_omzet: 0, omzet_bebas: 0, omzet_kena: 0, pph: 0, batas_bebas: 500000000, rincian: [] }, lainnya: { bruto: 0, pph: 0 }, total_pph: 0 },
  bukan_objek: 0, harta_total: 0, utang_total: 0, kekayaan_bersih: 0, tanggungan_list: [], total_pph_final: 0, total_pph: 0,
  kredit_pajak: { pph21_dipotong: 0, pph2223_dipotong: 0, pph25_angsuran: 0, pph24_luar_negeri: 0, total: 0 },
  pph_kurang_bayar: 0, pph_lebih_bayar: 0,
};

export default function PajakPribadi() {
  const [tahun, setTahun] = useState(new Date().getFullYear());
  const [d, setD] = useState(emptyData);

  const load = async () => { const r = await api.get("/laporan/pph-op", { params: { tahun } }); setD({ ...emptyData, ...r.data }); };
  useEffect(() => { load(); }, [tahun]);

  const umkm = d.final?.umkm || emptyData.final.umkm;

  return (
    <>
      <PageHeader title="Laporan Pajak Pribadi (SPT 1770)" subtitle="Ringkasan penghasilan & PPh Orang Pribadi — siap disalin ke Coretax DJP." actions={
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => download("/export/pph-op", { fmt: "pdf", tahun }, `spt-1770-${tahun}.pdf`)} className="rounded-sm" data-testid="pph-op-pdf"><Download size={14} className="mr-1" /> PDF</Button>
          <Button variant="outline" onClick={() => download("/export/pph-op", { fmt: "xlsx", tahun }, `spt-1770-${tahun}.xlsx`)} className="rounded-sm" data-testid="pph-op-xlsx"><FileSpreadsheet size={14} className="mr-1" /> Excel</Button>
        </div>
      } />
      <div className="p-4 md:p-8 space-y-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="card-flat p-4"><Label>Tahun Pajak</Label><Input type="number" className="w-32" value={tahun} onChange={(e) => setTahun(Number(e.target.value) || tahun)} data-testid="pph-op-tahun" /></div>
          <div className="card-flat p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Status PTKP</div><div className="mt-1 text-lg font-semibold">{d.ptkp?.status} <span className="text-sm text-slate-500 font-normal">({rupiah(d.ptkp?.total)})</span></div></div>
          <div className="card-flat p-4"><div className="text-[11px] uppercase tracking-widest text-slate-500">Metode Usaha</div><div className="mt-1 text-lg font-semibold uppercase">{d.metode_usaha}</div></div>
        </div>

        {/* Ringkasan total */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <div className="card-flat p-6" data-testid="card-pph-progresif"><div className="text-[11px] uppercase tracking-widest text-slate-500">PPh Terutang (Progresif)</div><div className="mt-3 font-mono text-2xl text-slate-900">{rupiah(d.pph_progresif)}</div></div>
          <div className="card-flat p-6" data-testid="card-pph-final"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total PPh Final</div><div className="mt-3 font-mono text-2xl text-amber-700">{rupiah(d.total_pph_final)}</div></div>
          <div className="card-flat p-6 border-l-4 border-l-emerald-500" data-testid="card-pph-total"><div className="text-[11px] uppercase tracking-widest text-slate-500">Total PPh</div><div className="mt-3 font-mono text-2xl text-emerald-700">{rupiah(d.total_pph)}</div></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title="A. Penghasilan Neto (Objek Progresif)">
            <Row label="Neto Gaji / Pekerjaan" value={rupiah(d.gaji_neto)} />
            <Row label={`\u2013 Bruto Gaji`} value={rupiah(d.gaji_bruto)} sub indent />
            <Row label={`\u2013 Biaya Jabatan (5% maks 6jt)`} value={rupiah(d.biaya_jabatan)} sub indent />
            <Row label={`Neto Usaha / Pekerjaan Bebas (${(d.metode_usaha || "").toUpperCase()})`} value={rupiah(d.usaha_neto)} />
            <Row label={`\u2013 Peredaran Bruto Usaha`} value={rupiah(d.usaha_bruto)} sub indent />
            <Row label="Neto Penghasilan Lain (non-final)" value={rupiah(d.penghasilan_neto_lain)} />
            <Row label="TOTAL PENGHASILAN NETO" value={rupiah(d.penghasilan_neto)} bold />
          </Section>

          <Section title="B / C. PTKP & Penghasilan Kena Pajak">
            <Row label={`PTKP (${d.ptkp?.status}, ${d.ptkp?.tanggungan} tanggungan)`} value={rupiah(d.ptkp?.total)} />
            <Row label="Penghasilan Kena Pajak (PKP)" value={rupiah(d.pkp)} bold />
            <div className="pt-2">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 mb-1">Lapisan Tarif Pasal 17</div>
              {(d.pph_progresif_lapisan || []).length === 0 && <div className="text-sm text-slate-400 py-1">Tidak ada pajak progresif.</div>}
              {(d.pph_progresif_lapisan || []).map((l, i) => (
                <Row key={i} label={`${Math.round(l.tarif_persen)}% \u00d7 ${rupiah(l.dasar)}`} value={rupiah(l.pajak)} sub />
              ))}
              <Row label="PPh Terutang (Progresif)" value={rupiah(d.pph_progresif)} bold />
            </div>
          </Section>
        </div>

        {/* PPh Final UMKM */}
        <div className="card-flat overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
            <span className="text-sm font-semibold">D. PPh Final UMKM 0,5% (PP 55/2022)</span>
            <span className="text-xs text-slate-500">Batas bebas: {rupiah(umkm.batas_bebas)}</span>
          </div>
          <div className="p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
            <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Total Omzet Usaha</div><div className="mt-1 font-mono text-lg">{rupiah(umkm.total_omzet)}</div></div>
            <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Omzet Bebas Pajak</div><div className="mt-1 font-mono text-lg text-emerald-700">{rupiah(umkm.omzet_bebas)}</div></div>
            <div><div className="text-[11px] uppercase tracking-wide text-slate-500">Omzet Kena PPh</div><div className="mt-1 font-mono text-lg">{rupiah(umkm.omzet_kena)}</div></div>
            <div><div className="text-[11px] uppercase tracking-wide text-slate-500">PPh Final 0,5%</div><div className="mt-1 font-mono text-lg text-amber-700 font-semibold">{rupiah(umkm.pph)}</div></div>
          </div>
          {(umkm.rincian || []).length > 0 && (
            <div className="overflow-x-auto border-t border-slate-100">
              <table className="w-full text-sm" data-testid="umkm-monthly">
                <thead><tr className="bg-slate-50 border-b border-slate-200 text-left text-[11px] uppercase tracking-wide text-slate-500">
                  <th className="p-3">Bulan</th><th className="p-3 text-right">Omzet</th><th className="p-3 text-right">Kena Pajak</th><th className="p-3 text-right">PPh 0,5%</th>
                </tr></thead>
                <tbody>
                  {umkm.rincian.map((r) => (
                    <tr key={r.bulan_key} className="border-b border-slate-100">
                      <td className="p-3">{r.bulan}</td>
                      <td className="p-3 text-right font-mono">{rupiah(r.omzet)}</td>
                      <td className="p-3 text-right font-mono">{rupiah(r.kena_pajak)}</td>
                      <td className="p-3 text-right font-mono font-semibold text-amber-700">{rupiah(r.pph)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title="PPh Final Lainnya & Bukan Objek">
            <Row label="Bruto PPh Final Lainnya" value={rupiah(d.final?.lainnya?.bruto)} />
            <Row label="PPh Final Lainnya" value={rupiah(d.final?.lainnya?.pph)} />
            <Row label="Penghasilan Bukan Objek Pajak" value={rupiah(d.bukan_objek)} />
          </Section>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title="F. Kredit Pajak & Status Pelunasan (PPh 29/28A)">
            <Row label="PPh Terutang (Progresif)" value={rupiah(d.pph_progresif)} />
            <Row label="PPh 21 Dipotong Pihak Lain (gaji PT)" value={rupiah(d.kredit_pajak?.pph21_dipotong)} sub />
            <Row label="PPh 22/23 Dipotong" value={rupiah(d.kredit_pajak?.pph2223_dipotong)} sub />
            <Row label="Angsuran PPh 25" value={rupiah(d.kredit_pajak?.pph25_angsuran)} sub />
            <Row label="Total Kredit Pajak" value={rupiah(d.kredit_pajak?.total)} />
            <Row label="PPh KURANG BAYAR (29)" value={rupiah(d.pph_kurang_bayar)} bold />
            <Row label="PPh LEBIH BAYAR (28A)" value={rupiah(d.pph_lebih_bayar)} bold />
          </Section>
          <Section title="G. Harta, Utang & Kekayaan Bersih (Akhir Tahun)">
            <Row label="Total Harta" value={rupiah(d.harta_total)} bold />
            <Row label="Total Utang" value={rupiah(d.utang_total)} bold />
            <Row label="Kekayaan Bersih (Harta − Utang)" value={rupiah(d.kekayaan_bersih)} bold />
            <Row label="Jumlah Tanggungan" value={String((d.tanggungan_list || []).length)} sub />
          </Section>
        </div>

        <div className="card-flat p-4 flex gap-3 border-l-4 border-l-blue-500">
          <Info size={18} className="text-blue-600 shrink-0" />
          <div className="text-sm text-slate-600">Angka pada laporan ini mengikuti struktur SPT 1770. Unduh PDF/Excel lalu salin ke Coretax DJP. Ubah status PTKP & metode usaha di menu <b>Pengaturan Pajak</b>.</div>
        </div>
      </div>
    </>
  );
}
